// Animated "🔥 New" pill shown next to sidebar entries (Shield / Unshield).
// Code-rendered replacement for the static new-badge.png: a red gradient pill
// that fires a short burst of radar-style "ping" ripples on mount to draw the
// eye, then goes quiet — no forever-loop on persistent sidebar chrome.
import { LinearGradient } from 'expo-linear-gradient'
import React, { useEffect, useRef } from 'react'
import { AccessibilityInfo, Animated, Easing, Text } from 'react-native'

// Pill fill sampled from the original asset (vertical red gradient).
const PILL_COLORS: readonly [string, string] = ['#FF5A5D', '#EE4042']
const GLOW = '#F5333A'

// A short, finite attention burst — ping a few times, then rest.
const PING_COUNT = 3
const EXPAND_MS = 900
const REST_MS = 350

const NewBadge: React.FC = () => {
  // Drives one outward ripple (0 → 1). resetBeforeIteration snaps it back for
  // the next ping, which is invisible because the ring is fully faded at t=1.
  const t = useRef(new Animated.Value(0)).current

  useEffect(() => {
    let loop: Animated.CompositeAnimation | undefined
    let cancelled = false

    // Respect reduced motion: keep the static pill, skip the ripple entirely.
    AccessibilityInfo.isReduceMotionEnabled().then((reduce) => {
      if (cancelled || reduce) return
      loop = Animated.loop(
        Animated.sequence([
          Animated.timing(t, {
            toValue: 1,
            duration: EXPAND_MS,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true
          }),
          // Beat of quiet between pings (ring is invisible here).
          Animated.delay(REST_MS)
        ]),
        { iterations: PING_COUNT }
      )
      loop.start()
    })

    return () => {
      cancelled = true
      loop?.stop()
    }
  }, [t])

  // Radar ring: born at the pill edge, expands outward as it fades out.
  const glowOpacity = t.interpolate({ inputRange: [0, 1], outputRange: [0.5, 0] })
  const glowScale = t.interpolate({ inputRange: [0, 1], outputRange: [1, 1.55] })

  return (
    <Animated.View style={{ alignSelf: 'flex-start' }}>
      <Animated.View
        pointerEvents="none"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 999,
          backgroundColor: GLOW,
          opacity: glowOpacity,
          transform: [{ scale: glowScale }]
        }}
      />
      <LinearGradient
        colors={PILL_COLORS}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 3,
          paddingHorizontal: 7,
          paddingVertical: 3,
          borderRadius: 999,
          // Static soft glow under the pill for depth (web: boxShadow).
          shadowColor: GLOW,
          shadowOffset: { width: 0, height: 0 },
          shadowOpacity: 0.6,
          shadowRadius: 6
        }}
      >
        <Text style={{ fontSize: 9, lineHeight: 14 }}>🔥</Text>
        <Text style={{ color: '#FFFFFF', fontSize: 10, fontWeight: '600', lineHeight: 14 }}>
          New
        </Text>
      </LinearGradient>
    </Animated.View>
  )
}

export default NewBadge
